import "yet-another-react-lightbox/styles.css";
import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import {
  EChatType,
  IChat,
  IChatGroup,
  IChatEmit,
} from "@deltasge/marauders-map";
import { Box, Grid } from "@mui/material";

import { useSocket } from "context/SocketContext";
import { useAppSelector } from "store";
import { ChatItem } from "components/ChatItem";
import { snack } from "components/GlobalSnackbar";
import { getError, sendS3File } from "utils";
import { isBefore, parseISO } from "date-fns";
import { ChatTextFieldComponent } from "./ChatTextFieldComponent";

export const ChatComponent: FC<{
  chatGroup: IChatGroup;
  setChatGroup: React.Dispatch<React.SetStateAction<IChatGroup | undefined>>;
}> = ({ chatGroup, setChatGroup }) => {
  const scroll = useRef<HTMLDivElement | null>(null);
  const [message, setMessage] = useState("");
  const [anexando, setAnexando] = useState(false);

  const {
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const socket = useSocket();

  const handleSendMsg = () => {
    try {
      if (!message) return;

      const chat: IChat = {
        chatType: EChatType.Text,
        context: message,
        fromId: usuario?.deltaId ?? "",
        viewed: [],
        delivered: [],
      };
      const chatEmit: IChatEmit = {
        id: chatGroup._id,
        chat,
      };
      socket?.emit("chat:msg:v3", chatEmit);

      setMessage("");
      if (scroll.current) {
        scroll.current.scrollTop = scroll.current.scrollHeight;
      }
    } catch (err) {
      snack.warning(getError(err));
    }
  };

  const handleAttachment = useCallback(
    async ({ file, chatType }: { file: File; chatType: EChatType }) => {
      try {
        setAnexando(true);
        const arquivo = await sendS3File({
          file,
          categoria: "chat",
        });
        if (!arquivo.id) {
          throw new Error("Erro ao obter o ID do arquivo");
        }
        const chat: IChat = {
          chatType,
          context: arquivo.caminhoArquivo,
          fromId: usuario?.deltaId ?? "",
          viewed: [],
          delivered: [],
        };
        const chatEmit: IChatEmit = {
          id: chatGroup._id,
          chat,
        };
        socket?.emit("chat:msg:v3", chatEmit);
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setAnexando(false);
      }
    },
    [chatGroup._id, socket, usuario]
  );

  const setDelivered = ({
    id,
    chatGroup,
  }: {
    id?: string;
    chatGroup: IChatGroup;
  }) => {
    socket?.emit("chat:setDelivered", {
      chatGroupId: chatGroup._id,
      deltaId: usuario?.deltaId,
      chatId: id,
    });
  };

  const setViewed = useCallback(
    ({ id, chatGroup }: { id?: string; chatGroup: IChatGroup }) => {
      socket?.emit("chat:setViewed:v2", {
        chatGroupId: chatGroup._id,
        deltaId: usuario?.deltaId,
        chatId: id,
      });
    },
    [socket, usuario]
  );

  const alertUser = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    return (event.returnValue = "Deseja fechar esta conversa?");
  };

  const handleChat = (data: IChat, chatGroup: IChatGroup) => {
    if (
      data.fromId != usuario?.deltaId &&
      !data.delivered.find((f) => f.deltaId == usuario?.deltaId)
    ) {
      setDelivered({ id: data._id, chatGroup: chatGroup });
    }

    if (
      data.fromId != usuario?.deltaId &&
      !data.viewed?.find((f) => f.deltaId == usuario?.deltaId)
    ) {
      setViewed({ id: data._id, chatGroup });
    }

    setChatGroup((prev) => {
      const chats: IChat[] = [];
      if (prev?.chats) chats.push(...prev.chats);

      const index = chats.findIndex((f) => f._id == data._id);
      if (index >= 0) chats[index] = data;
      else chats.unshift(data);

      return {
        ...chatGroup,
        chats,
      };
    });
    if (scroll.current) {
      scroll.current.scrollTop = scroll.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (scroll.current && message == "") {
      scroll.current.scrollTop = scroll.current.scrollHeight;
    }
  }, [scroll.current, message]);

  useEffect(() => {
    if (scroll.current) {
      scroll.current.scrollTop = scroll.current.scrollHeight;
    }

    if (socket && chatGroup._id) {
      socket.emit("chat:joinGroup", {
        chatGroupId: chatGroup._id,
        deltaId: usuario?.deltaId,
      });
      socket.on(chatGroup._id, (chat: IChat) => handleChat(chat, chatGroup));
    }

    if (
      chatGroup.chats &&
      chatGroup.chats.find(
        (f) =>
          !f.delivered.map((m) => m.deltaId).includes(usuario?.deltaId ?? "")
      )
    ) {
      setDelivered({ chatGroup });
    }

    if (
      chatGroup.chats &&
      chatGroup.chats.find(
        (f) => !f.viewed.map((m) => m.deltaId).includes(usuario?.deltaId ?? "")
      )
    ) {
      setViewed({ chatGroup });
    }

    window.addEventListener("beforeunload", alertUser);
    return () => {
      window.removeEventListener("beforeunload", alertUser);
      socket?.emit("chat:leaveGroup", {
        chatGroupId: chatGroup._id,
        deltaId: usuario?.deltaId,
      });
      socket?.off(chatGroup._id);
    };
  }, []);

  const deleted = chatGroup.deleted.find((f) => f.deltaId == usuario?.deltaId);

  return (
    <Box sx={{ height: "100%", p: 1 }}>
      <Box
        sx={{
          height: (theme) => `calc(100% - ${theme.spacing(6)})`,
          pb: 1,
        }}
      >
        <Box ref={scroll} sx={{ height: "100%", overflow: "auto" }}>
          <Grid
            className="chat-messages"
            container
            direction="column-reverse"
            flexWrap="nowrap"
            justifyContent="flex-end"
            sx={{ height: "100%" }}
          >
            {(chatGroup.chats ?? [])
              .filter((chat) => {
                if (!deleted) return true;
                const createdAt = parseISO((chat.createdAt || "").toString());
                const deletedAt = parseISO(deleted.at.toString());
                return isBefore(deletedAt, createdAt);
              })
              .map((m, i) => (
                <ChatItem
                  key={`${i}_${m._id}`}
                  chat={m}
                  user={chatGroup.chatUsers?.find((f) => f.deltaId == m.fromId)}
                  isMe={usuario?.deltaId == m.fromId}
                />
              ))}
          </Grid>
        </Box>
      </Box>
      <ChatTextFieldComponent
        anexando={anexando}
        message={message}
        setMessage={setMessage}
        handleSendMsg={handleSendMsg}
        handleAttachment={handleAttachment}
        disabled={!socket?.connected}
      />
    </Box>
  );
};
